import { useState, useEffect, FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';

const testimonials = [
  {
    name: '王先生',
    role: '资深游戏玩家',
    content: 'pg模拟器真的太方便了，不用下载就能玩，手机电脑都能用。游戏画面很流畅，操作也很简单，推荐给所有喜欢PG游戏的朋友！',
    rating: 5,
  },
  {
    name: '李女士',
    role: '休闲游戏爱好者',
    content: '作为一个不太懂技术的玩家，pg模拟器的简单操作让我很惊喜。打开网页就能玩，完全不需要复杂的设置，真心好评！',
    rating: 5,
  },
  {
    name: '张先生',
    role: '游戏社区管理员',
    content: '我们游戏社区很多成员都在用pg模拟器，反馈都非常好。平台游戏种类多，更新快，客服响应也很快，值得信赖。',
    rating: 5,
  },
  {
    name: '刘女士',
    role: '自由职业者',
    content: '工作之余喜欢用pg模拟器放松一下，手机版特别方便，通勤路上也能玩。画面精美，游戏种类丰富，已经推荐给很多同事了。',
    rating: 4,
  },
  {
    name: '陈先生',
    role: 'IT工程师',
    content: '从技术角度看，pg模拟器的性能优化做得很好，即使在配置一般的设备上也能流畅运行。云端同步功能也很实用。',
    rating: 5,
  },
  {
    name: '赵女士',
    role: '大学生',
    content: '宿舍电脑配置不高，但pg模拟器在线版运行完全没问题。和室友一起玩特别开心，而且完全免费，对学生党太友好了！',
    rating: 5,
  },
];

export default function About() {
  const location = useLocation();
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }
    formData.delete('phone_alt');

    setFormStatus('loading');
    setFormMessage('');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9sqp0l8o1vn02ekukj0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormStatus('error');
          setFormMessage('提交失败，请稍后重试。');
        } else {
          setFormStatus('error');
          setFormMessage(serverMsg || '提交失败，请稍后重试。');
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查连接后重试。');
    }
  };

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于PG模拟器',
    description: 'PG模拟器是专为游戏爱好者打造的在线模拟体验平台，了解我们的品牌故事、口碑评价与联系方式。',
    url: 'https://www.pg-simulator.com/about',
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutJsonLd)}</script>
      <Navbar />
      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="bg-background-100 border-b border-background-200">
          <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />
            <div className="pb-8">
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-2">关于我们</h1>
              <p className="text-foreground-600 text-sm">了解PG模拟器的品牌故事与发展历程</p>
            </div>
          </div>
        </div>

        {/* Brand Introduction */}
        <section className="py-16 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-5">
                  品牌介绍
                </h2>
                <div className="space-y-4 text-foreground-700 text-sm leading-relaxed">
                  <p>
                    <strong>PG模拟器</strong>成立于2024年，是国内领先的在线游戏模拟体验平台。
                    我们致力于为游戏爱好者提供<strong>无需下载、即点即玩</strong>的PG系列游戏体验。
                  </p>
                  <p>
                    经过两年的发展，pg模拟器已经从最初的单一游戏模拟工具，成长为涵盖八大游戏分类、
                    200+款精品游戏的综合性<strong>pg游戏模拟器</strong>平台。我们的用户遍布全国各地，
                    累计服务超过<strong>50万</strong>活跃玩家。
                  </p>
                  <p>
                    我们始终坚持<strong>"让每个人都享受游戏的乐趣"</strong>的理念，持续优化产品体验，
                    不断丰富游戏内容。未来，pg模拟器将继续深耕在线游戏模拟领域，为玩家带来更多优质的游戏内容。
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-6 mt-8">
                  <div className="text-center">
                    <div className="text-2xl font-heading text-primary-500">200+</div>
                    <div className="text-xs text-foreground-500 mt-1">精品游戏</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-heading text-primary-500">50万+</div>
                    <div className="text-xs text-foreground-500 mt-1">活跃玩家</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-heading text-primary-500">4.9</div>
                    <div className="text-xs text-foreground-500 mt-1">用户评分</div>
                  </div>
                </div>
              </div>

              <div className="w-full aspect-[4/3] rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20tech%20office%20with%20creative%20team%20collaborating%2C%20warm%20ambient%20lighting%2C%20gaming%20posters%20on%20wall%2C%20friendly%20atmosphere%2C%20clean%20contemporary%20design%2C%20orange%20and%20cream%20color%20accents&width=800&height=600&seq=about-brand-image&orientation=landscape"
                  alt="PG模拟器团队"
                  title="PG模拟器团队 - pg模拟器官网"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="py-16 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
                玩家口碑
              </h2>
              <p className="text-foreground-600 text-sm">来自真实玩家的评价与反馈</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {testimonials.map((t, i) => (
                <div key={i} className="bg-background-50 rounded-xl p-5">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, j) => (
                      <i
                        key={j}
                        className={`text-sm ${j < t.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="font-heading text-sm text-primary-600">{t.name.charAt(0)}</span>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-16 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-5">
                  联系我们
                </h2>
                <p className="text-foreground-600 text-sm mb-8 leading-relaxed">
                  有任何问题或建议？请通过以下方式联系我们，或填写右侧表单，我们将尽快回复。
                </p>

                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                      <i className="ri-mail-line text-lg text-primary-500"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800 mb-0.5">电子邮箱</div>
                      <span className="text-sm text-foreground-600">support@pg-simulator.com</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                      <i className="ri-phone-line text-lg text-primary-500"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800 mb-0.5">客服热线</div>
                      <span className="text-sm text-foreground-600">400-888-6666</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                      <i className="ri-map-pin-line text-lg text-primary-500"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800 mb-0.5">公司地址</div>
                      <span className="text-sm text-foreground-600">北京市朝阳区科技园区创新大厦12层</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                      <i className="ri-time-line text-lg text-primary-500"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800 mb-0.5">工作时间</div>
                      <span className="text-sm text-foreground-600">周一至周五 9:00 - 18:00</span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="bg-background-100 rounded-xl p-6"
                >
                  <h3 className="font-heading text-lg text-foreground-900 mb-5">发送消息</h3>

                  {formStatus === 'success' && (
                    <div className="mb-4 px-4 py-3 bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg">
                      <i className="ri-check-line mr-1.5"></i>{formMessage}
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">
                      <i className="ri-error-warning-line mr-1.5"></i>{formMessage}
                    </div>
                  )}

                  <div className="space-y-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言内容 <span className="text-primary-500">*</span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言（最多500字）"
                      ></textarea>
                      <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                    </div>

                    <input
                      type="text"
                      name="phone_alt"
                      className="honeypot-field"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />

                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full px-6 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white font-medium text-sm rounded-lg whitespace-nowrap cursor-pointer transition-colors"
                    >
                      {formStatus === 'loading' ? (
                        <span className="flex items-center justify-center gap-2">
                          <i className="ri-loader-4-line animate-spin"></i>发送中...
                        </span>
                      ) : (
                        '发送消息'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}